// import { useLayoutEffect, useState } from 'react';
import { useMemo, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Typography, useMediaQuery } from '@mui/material';

// project-imports
import NavGroup from './NavGroup';
// import menuItem from 'menu-items';
import baseMenuConfig from 'menu-items';
import useAuth from 'hooks/useAuth';
import { useSelector } from 'store';
import useConfig from 'hooks/useConfig';
import { HORIZONTAL_MAX_ITEM } from 'config';
import { isCountryMarketContextCode } from 'utils/countryMarket';
import { isTrialExclPlan } from 'utils/trialPlans';

// types
import { NavItemType } from 'types/menu';
import { MenuOrientation } from 'types/config';
// import { UserAdd } from 'iconsax-react';

export const ACCOUNT_GROUP_ID = 'account-group';
export const OVERVIEW_GROUP_ID = 'overview-group';
export const DEVELOPMENT_GROUP_ID = 'development-group';
export const DEVELOPMENT_COMPANY_GROUP_ID = 'development-company-group';
export const CUSTOMER_GROUP_ID = 'customer-group';
export const TOOLS_GROUP_ID = 'tools-group';
export const DISCOVERY_TRIP_GROUP_ID = 'discovery-trip-group';
export const MARKETPLACE_GROUP_ID = 'marketplace-group';
export const OTHERS_GROUP_ID = 'others-group';

// ==============================|| MENU - REMOVE ITEMS ||============================== //

type MenuConfig = { items: NavItemType[] };
type RemoveChildrenSpec = { groupId: string; childId: string };

function pruneMenu(
  base: MenuConfig,
  { removeGroups = [], removeChildren = [] }: { removeGroups?: string[]; removeChildren?: RemoveChildrenSpec[] }
): MenuConfig {
  const groupsToRemove = new Set(removeGroups);

  // Mapa de -> groupId: Set(childId, ...)
  const childrenMap = new Map<string, Set<string>>();
  for (const { groupId, childId } of removeChildren) {
    const set = childrenMap.get(groupId) ?? new Set<string>();
    set.add(childId);
    childrenMap.set(groupId, set);
  }

  const visit = (node: NavItemType, parent?: NavItemType): NavItemType | null => {
    if (!node?.id) return node;

    // 1) si el propio node es un grupo a eliminar
    if (groupsToRemove.has(node.id)) return null;

    // 2) procesar hijos de forma recursiva
    let nextChildren = node.children?.map((c) => visit(c, node)).filter(Boolean) as NavItemType[] | undefined;

    // 3) si hay especificación para este grupo, filtra sus hijos por id
    const toDrop = childrenMap.get(node.id);
    if (toDrop && nextChildren) {
      nextChildren = nextChildren.filter((c) => c.id && !toDrop.has(c.id));
    }

    // 4) eliminar grupos/colapsables vacíos tras la poda
    if ((node.type === 'group' || node.type === 'collapse') && (!nextChildren || nextChildren.length === 0)) {
      return null;
    }

    // 5) devolver clon inmutable (preserva React elements)
    return nextChildren ? { ...node, children: nextChildren } : { ...node };
  };

  const items = base.items.map((g) => visit(g)).filter(Boolean) as NavItemType[];
  return { items };
}
/** Marcar deshabilitados (para trial-excl) – NO elimina nada, solo setea `disabled` en items no permitidos */
function markDisabledMenuIdsOnly(
  base: MenuConfig,
  { allowGroups = [], allowItems = [] }: { allowGroups?: string[]; allowItems?: string[] }
): MenuConfig {
  const gSet = new Set(allowGroups);
  const iSet = new Set(allowItems);

  const visit = (node: NavItemType, allowedByAncestor = false): NavItemType => {
    const allowedHere = allowedByAncestor || (node.id ? gSet.has(node.id) || iSet.has(node.id) : false);

    if (node.type === 'item') {
      return { ...node, disabled: !allowedHere };
    }

    const passDown = allowedByAncestor || (node.id ? gSet.has(node.id) : false);
    const children = node.children?.map((c) => visit(c, passDown)) as NavItemType[] | undefined;

    return children ? { ...node, children } : { ...node };
  };

  return { items: base.items.map((g) => visit(g)) };
}

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

const Navigation = () => {
  const theme = useTheme();

  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const { menuOrientation } = useConfig();
  const { drawerOpen } = useSelector((state) => state.menu);
  const { user } = useAuth();

  const [selectedItems, setSelectedItems] = useState<string | undefined>('');
  const [selectedLevel, setSelectedLevel] = useState<number>(0);
  // const [menuItems, setMenuItems] = useState<{ items: NavItemType[] }>({ items: [] });

  // useLayoutEffect(() => {
  //   setMenuItems(menuItem);
  //   // eslint-disable-next-line
  // }, [menuItem]);

  // Para añadir nuevos items en función de los productos contratados
  // useLayoutEffect(() => {
  //   const hasExtra = user?.appProducts ? !!JSON.parse(user.appProducts)?.includes('extra_user') : false;

  //   // Construimos un nuevo array de items SIN serializar (se preservan React elements)
  //   const nextItems = baseMenuConfig.items.map((group) => {
  //     if (group.id !== ACCOUNT_GROUP_ID) return group;

  //     const children = Array.isArray(group.children) ? [...group.children] : [];

  //     // elimina residuo previo si re-renderiza
  //     const filtered = children.filter((c: any) => c.id !== 'account-users');

  //     if (hasExtra) {
  //       filtered.push({
  //         id: 'account-users',
  //         type: 'item',
  //         title: 'Users',
  //         url: '/account/users',
  //         icon: UserAdd
  //       } as unknown as NavItemType);
  //     }

  //     // devuelve un NUEVO objeto para el grupo account (inmutable)
  //     return { ...group, children: filtered };
  //   });

  //   setMenuItems({ items: nextItems });
  // }, [user]);

  // Para eliminar items en función de los productos contratados (principalmente para usuarios extra)
  // const computedMenu = useMemo(() => {
  //   const removeGroups: string[] = [];
  //   const removeChildren: RemoveChildrenSpec[] = [];

  //   // console.log(user);

  //   // // productos del propio usuario
  //   // const hasExtra = user?.appProducts
  //   //   ? (() => {
  //   //       try {
  //   //         return JSON.parse(user.appProducts).includes('extra_user');
  //   //       } catch {
  //   //         return false;
  //   //       }
  //   //     })()
  //   //   : false;

  //   // // si NO tiene extra_user, ocultamos el item "users" dentro del grupo account
  //   // if (!hasExtra) {
  //   //   removeChildren.push({ groupId: ACCOUNT_GROUP_ID, childId: 'account-users' });
  //   // }

  //   // productos del propio usuario
  //   const appProducts = user?.appProducts
  //     ? (() => {
  //         try {
  //           return JSON.parse(user.appProducts);
  //         } catch {
  //           return [];
  //         }
  //       })()
  //     : [];

  //   false && console.log(appProducts);

  //   // si NO tiene extra_user, ocultamos el item "users" dentro del grupo account
  //   // if (!hasExtra) {
  //   //   removeChildren.push({ groupId: ACCOUNT_GROUP_ID, childId: 'account-users' });
  //   // }

  //   // si es usuario "hijo" (extra), puedes ocultar grupos enteros o items concretos:
  //   if (user?.isChild) {
  //     // productos del padre
  //     const parentAppProducts = user?.parentAppProducts
  //       ? (() => {
  //           try {
  //             return JSON.parse(user.parentAppProducts);
  //           } catch {
  //             return [];
  //           }
  //         })()
  //       : [];

  //     // console.log(parentAppProducts);

  //     // quitar grupo completo
  //     removeGroups.push(MARKETPLACE_GROUP_ID); // <- id del grupo
  //     if (!parentAppProducts.includes('extra_feed')) {
  //       // quitar item de grupo concreto
  //       removeChildren.push({ groupId: TOOLS_GROUP_ID, childId: 'feed' });
  //     }
  //   }

  //   // Aplica la poda
  //   return pruneMenu(baseMenuConfig, { removeGroups, removeChildren });
  // }, [user]);
  const isTrialExcl = isTrialExclPlan(user?.currentPlan);
  const isTrial = user?.currentPlan?.includes('trial');
  const isCountryMarketContext = isCountryMarketContextCode(user?.code) || isCountryMarketContextCode(user?.parentCode);

  const computedMenu = useMemo(() => {
    if (isTrialExcl) {
      // 🔒 MODO TRIAL-EXCL: no podar, solo deshabilitar lo NO permitido
      // aquí decides qué grupos y/o items quedan ACTIVOS (el resto quedará disabled)
      return markDisabledMenuIdsOnly(baseMenuConfig, {
        // grupos completos activos:
        // allowGroups: [DEVELOPMENT_GROUP_ID, DEVELOPMENT_COMPANY_GROUP_ID, ACCOUNT_GROUP_ID],
        allowGroups: [ACCOUNT_GROUP_ID],
        // items concretos activos (ids de item):
        allowItems: ['development-card', 'documentation']
      });
    }

    // ✅ MODO NORMAL: tu lógica actual de poda (ocultar cosas para hijo / productos, etc.)
    const removeGroups: string[] = [];
    const removeChildren: RemoveChildrenSpec[] = [];

    if (user?.isChild) {
      removeGroups.push(MARKETPLACE_GROUP_ID);

      const parentAppProducts = user?.parentAppProducts
        ? (() => {
            try {
              return JSON.parse(user.parentAppProducts);
            } catch {
              return [];
            }
          })()
        : [];

      if (!parentAppProducts.includes('extra_feed')) {
        removeChildren.push({ groupId: TOOLS_GROUP_ID, childId: 'feed' });
      }
    } else {
      const hasExtra = user?.appProducts
        ? (() => {
            try {
              return JSON.parse(user.appProducts).includes('extra_user');
            } catch {
              return false;
            }
          })()
        : false;

      if (!hasExtra) {
        removeChildren.push({ groupId: ACCOUNT_GROUP_ID, childId: 'account-users' });
      }
    }

    if (isTrial) {
      removeChildren.push({ groupId: DEVELOPMENT_GROUP_ID, childId: 'development-special' });
      removeChildren.push({ groupId: DEVELOPMENT_COMPANY_GROUP_ID, childId: 'developmentCompanySpecial' });
    }

    if (isCountryMarketContext) {
      removeGroups.push(MARKETPLACE_GROUP_ID);
    }

    // Permissions for AI
    // const hasAIPack = user?.appProducts
    //   ? (() => {
    //       try {
    //         return JSON.parse(user.appProducts).includes('ai_base');
    //       } catch {
    //         return false;
    //       }
    //     })()
    //   : false;
    const hasAIPack = user?.appProducts
      ? (() => {
          try {
            const list = JSON.parse(user.appProducts);
            return Array.isArray(list) && list.some((item) => typeof item === 'string' && item.includes('ai_base'));
          } catch {
            return false;
          }
        })()
      : false;

    if (!hasAIPack) {
      removeChildren.push({ groupId: TOOLS_GROUP_ID, childId: 'ai' });
    }

    // Permissions for DT
    if (user?.permissions !== 'dt') {
      removeGroups.push(DISCOVERY_TRIP_GROUP_ID);
    }

    return pruneMenu(baseMenuConfig, { removeGroups, removeChildren });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCountryMarketContext, isTrialExcl, user?.isChild, user?.appProducts, user?.parentAppProducts, isTrial]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  // let lastItemIndex = menuItems.items.length - 1;
  let lastItemIndex = computedMenu.items.length - 1;
  let remItems: NavItemType[] = [];
  let lastItemId: string;

  // if (lastItem && lastItem < menuItems.items.length) {
  if (lastItem && lastItem < computedMenu.items.length) {
    // lastItemId = menuItems.items[lastItem - 1].id!;
    lastItemId = computedMenu.items[lastItem - 1].id!;
    lastItemIndex = lastItem - 1;
    // remItems = menuItems.items.slice(lastItem - 1, menuItems.items.length).map((item) => ({
    remItems = computedMenu.items.slice(lastItem - 1).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon
    }));
  }

  // const navGroups = menuItems.items.slice(0, lastItemIndex + 1).map((item) => {
  const navGroups = computedMenu.items.slice(0, lastItemIndex + 1).map((item) => {
    switch (item.type) {
      case 'group':
        return (
          <NavGroup
            key={item.id}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedItems={selectedItems}
            lastItem={lastItem!}
            remItems={remItems}
            lastItemId={lastItemId}
            item={item}
          />
        );
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Fix - Navigation Group
          </Typography>
        );
    }
  });
  return (
    <Box
      sx={{
        pt: drawerOpen ? (isHorizontal ? 0 : 2) : 0,
        '& > ul:first-of-type': { mt: 0 },
        display: isHorizontal ? { xs: 'block', lg: 'flex' } : 'block'
      }}
    >
      {navGroups}
    </Box>
  );
};

export default Navigation;
